import * as React from 'react';

import cn from 'classnames';
import pluralize from 'pluralize';

import { Button, Pill, Text } from '@components/common';
import { AUTH_METHOD_NAMES } from '@components/GovernanceApp/components/constants';
import { WarningSVG } from '@components/svgs';
import { useCurrentAuthMethod } from '@hooks/useCurrentAuthMethod';

import { BuildingSvg } from './svgs';

export interface Props {
  account: PartialAccount;
  current: boolean;
  onSelect: (id: number) => void;
  handleSignOut: () => void;
}

export const Account: React.FC<React.PropsWithChildren<Props>> = ({ account, current, onSelect, handleSignOut }) => {
  const currentAuthMethod = useCurrentAuthMethod();

  const authMethodMismatch: boolean = !!(
    currentAuthMethod &&
    Array.isArray(account.auth_methods) &&
    !account.auth_methods.includes(currentAuthMethod)
  );

  const disabled: boolean = account.status === 'disabled';

  const handleOnSelect = () => {
    if (authMethodMismatch) {
      handleSignOut();
    } else {
      onSelect(account.id);
    }
  };

  return (
    <li
      className={cn('flex flex-col items-center border border-gray-200 p-8', {
        'opacity-50': disabled
      })}
    >
      <div className='flex w-full flex-row'>
        {account.logo ? (
          <div className='mr-4 flex w-16 items-center justify-center'>
            <img alt={`${account.name} logo`} src={account.logo} className='w-block' />
          </div>
        ) : (
          <div className='mr-4 flex h-14 w-16 items-center justify-center rounded border border-gray-200 bg-gray-50'>
            <BuildingSvg />
          </div>
        )}
        <div className='flex-grow'>
          <div className='mb-1 flex items-center space-x-6'>
            <Text h='700'>{account.name}</Text>
            {account.sandbox && <Pill color='orange'>Demo</Pill>}
          </div>
          {account.workspace_name && (
            <Text h='500' className='-mt-1' mb='1'>
              {account.workspace_name}
            </Text>
          )}
          <Text h='400' className='pb-1.5' color='gray-500'>
            {pluralize('members', account.team.length, true)}. Created by {account.owner.name}
          </Text>
        </div>
        <div className='flex flex-row items-center'>
          {current ? (
            <Button onClick={handleOnSelect} className='min-h-0 py-0'>
              Current
            </Button>
          ) : (
            <Button primary disabled={disabled} onClick={handleOnSelect} className='min-h-0 py-0'>
              {disabled ? 'Disabled' : 'Select'}
            </Button>
          )}
        </div>
      </div>
      {authMethodMismatch && (
        <Text h='400' color='yellow-600' className='mt-2 flex items-center'>
          <WarningSVG className='mr-2 h-10 w-10' />
          <span>
            This workspace requires authentication via{' '}
            {account.auth_methods.map((method) => AUTH_METHOD_NAMES[method]).join(' or ')}. If you choose to continue,
            you'll be signed out and redirected to log in.
          </span>
        </Text>
      )}
    </li>
  );
};
